import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { fromPromise } from 'xstate';

import { useNoticeBannersStackContext } from '@sg/garnish';

import {
  type LeaveGroupOrderParams,
  type LeaveGroupOrderResponse,
  type LeaveGroupOrderErrorParams,
  type OrderingMachineAction,
} from '@order/features-ordering';

import {
  type BagLeaveGroupOrderMutation,
  useBagLeaveGroupOrderMutation,
} from '../../GraphQL/BagGroupOrder.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useLeaveGroupOrder = () => {
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const [, executeLeaveGroupOrder] = useBagLeaveGroupOrderMutation();

  // ───────────────────────────────────────────────────────────────────────────

  const leaveGroupOrder = useMemo(
    () =>
      fromPromise<LeaveGroupOrderResponse, LeaveGroupOrderParams>(async () => {
        const response = await executeLeaveGroupOrder({});
        const result = response.data?.leaveMultipartyOrder;

        if (result?.__typename === 'LeaveOrderSuccess' && result.success) {
          return { success: true };
        }

        const responseError = getResponseError({ result }) ?? response.error?.message;

        throw new Error(responseError);
      }),
    [executeLeaveGroupOrder],
  );

  const handleLeaveGroupOrderError = useCallback<OrderingMachineAction<LeaveGroupOrderErrorParams>>(
    (_args, params) => {
      const fallbackErrorMessage = params.isHost ? messages.cancelError : messages.leaveError;

      addNoticeBanner({
        text: params.errorMessage || formatMessage(fallbackErrorMessage),
        palette: 'caution',
      });
    },
    [addNoticeBanner, formatMessage],
  );

  return { leaveGroupOrder, handleLeaveGroupOrderError };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getResponseError(params: GetResponseErrorParams) {
  const { result } = params;

  if (!result) return;

  if (result.__typename === 'MultipartyOrderNotFound' || result.__typename === 'OrderLocked') {
    return result.errorMessage;
  }

  if (result.__typename === 'ValidationError') {
    return result.message;
  }
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  cancelError: {
    defaultMessage: 'We couldn’t cancel the group order. Please try again.',
    description: 'Bag | Group order | Cancel error',
  },
  leaveError: {
    defaultMessage: 'We couldn’t leave the group order. Please try again.',
    description: 'Bag | Group order | Leave error',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GetResponseErrorParams = {
  result: BagLeaveGroupOrderMutation['leaveMultipartyOrder'] | undefined;
};
